/*
 * File: AddBuyerModal.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 15 Mar 2022 1:24:00 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { PlusOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Modal } from 'antd';
import { useFormikContext } from 'formik';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useCreateBuyerMutation } from 'redux/services/useBeautifylDayMonsterService';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormModel {
  firstName: string;
  lastName: string;
}

export const AddBuyerModal = ({ show, onClose }: Props): JSX.Element => {
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const { setValues, values: formikValues } = useFormikContext<SalesOrderPayloadModel>();

  const [form] = Form.useForm<{ firstName: string; lastName: string; dataAreaId: string }>();

  /* ****************** API Hooks ****************** */
  const [createBuyer, { isLoading, error, reset }] = useCreateBuyerMutation();

  /* ****************** Functions ****************** */
  const handleFormSubmit = async (values: FormModel): Promise<void> => {
    try {
      const data = await createBuyer({
        buyerData: {
          ...values,
          ...{
            dataAreaId: dataAreaId ?? ''
          }
        },
        customerId: formikValues.customerId
      }).unwrap();

      setValues((prevState) => ({
        ...prevState,
        buyerId: data.buyerId,
        buyerName: data.buyerName
      }));

      form.resetFields();
      reset();
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleClose = (): void => {
    onClose();
    form.resetFields();
    reset();
  };

  /* ****************** Render ****************** */
  return (
    <Modal
      centered={true}
      confirmLoading={isLoading}
      okButtonProps={{ icon: <PlusOutlined /> }}
      okText="Add Buyer"
      onCancel={handleClose}
      onOk={(): void => form.submit()}
      title="Add Buyer"
      open={show}>
      {error && <Alert style={{ marginBottom: 20 }} message={(error as any).data ?? 'Server error'} type="error" />}
      <Form layout="horizontal" form={form} onFinish={handleFormSubmit}>
        <Form.Item name="firstName" rules={[{ required: true, message: 'Please enter first name' }]}>
          <Input placeholder="First Name" />
        </Form.Item>
        <Form.Item name="lastName" rules={[{ required: true, message: 'Please enter last name' }]}>
          <Input placeholder="Last Name" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
